import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ClientRequestSubReasonSelect from './ClientRequestSubReasonSelect';
import {
  REASON_CLIENT_REQUEST,
  buildClientRequestReason,
  isClientRequestSubReasonMissing,
} from './clientRequestSubReasons';
import { ClaimPage, ClaimFilters, fetchClaims, sendLOC, cancelClaim, reinstateClaim, fetchReinstatementPaymentImpact, Claim, CancelClaimRequest, CancelClaimResponse, fetchCancelPreview, CancelPreviewClaimEntry, ReinstatementPaymentImpact, ReinstatementPaymentResult, RemainingPaymentAction, heldCancellationAlert, heldReviewTaskId, PHONE_REVIEW_QUESTION } from '../api/claims';
import { ClientGroup, ClientGroupStats } from '../types/clientGroup';
import { groupClaimsByClient, calculateGroupStats, filterClientGroups } from '../utils/claimGrouping';
import { canSendFirstLoc } from '../utils/bulkSubmission';
import { ClientGroupRow } from './ClientGroupRow';
import { useAuth } from '../context/AuthContext';
import { sendIdRequest, photoIdApi } from '../api/photoId';
import Modal from './ModalTrue';
import { HoldReviewModal } from './HoldReviewModal';
import ReinstatementPaymentModal from './ReinstatementPaymentModal';
import { reinstatementPaymentMessage } from './reinstatementPaymentMessage';
import NonCooperationReasonsFieldset from './NonCooperationReasonsFieldset';
import { NonCooperationReasonCode } from '../constants/nonCooperationClosure';
import { BulkPreviewTotals, formatPounds, summariseBulkFee } from './bulkCancelFee';
import { IdRequestStatus } from './IdRequestHistory';
import { claimSaysClientDeceased, IdRequestFields } from '../utils/idRequestHistory';
import './HierarchicalClaimsTable.css';

interface HierarchicalClaimsTableProps {
  onSelectClaim: (claim: Claim | null) => void;
  onShowDocs: (claim: Claim) => void;
  onCancelClaim?: (claimId: string, claimEmail?: string) => Promise<void> | void;
}

export const HierarchicalClaimsTable: React.FC<HierarchicalClaimsTableProps> = ({
  onSelectClaim,
  onShowDocs,
  onCancelClaim
}) => {
  // State
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());
  // Non-viable (disabled) lender block when reinstating. Holds the claim + lender awaiting an admin override decision.
  const [lenderBlockModal, setLenderBlockModal] = useState<{ claimId: string; lenderName: string } | null>(null);
  const [reinstatementPaymentPrompt, setReinstatementPaymentPrompt] = useState<{
    impact: ReinstatementPaymentImpact;
    force: boolean;
  } | null>(null);
  const [reinstatementPaymentSubmitting, setReinstatementPaymentSubmitting] = useState(false);
  const [phoneReviewTask, setPhoneReviewTask] = useState<{
    taskId: string;
    requestedReason: string | null;
    clientEmail: string | null;
  } | null>(null);
  
  type PaymentPreview = {
    chargeableClaimIds: string[];
    chargeableClaimCount: number;
    estimatedAmount?: number;
    currency?: string;
  };

  const [showBypassModal, setShowBypassModal] = useState(false);
  const [pendingBulkCancellation, setPendingBulkCancellation] = useState<{
    group: ClientGroup;
    claims: Claim[];
    reason: string;
    nonCooperationReasonCodes?: NonCooperationReasonCode[];
    requiresPaymentDecision: boolean;
    paymentPreview?: PaymentPreview;
  } | null>(null);

  // Filters and sorting
  const [showAuditedOnly, setShowAuditedOnly] = useState(false);
  const [showDeceasedOnly, setShowDeceasedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { apiRequest, isAdmin } = useAuth();
  const [pageSize, setPageSize] = useState(50); // Reduce initial page size for faster loading

  const [showBulkReasonModal, setShowBulkReasonModal] = useState(false);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [bulkReasonDraft, setBulkReasonDraft] = useState('');
  const [bulkSelectedReason, setBulkSelectedReason] = useState<string>('Client request');
  const [bulkClientRequestSubReason, setBulkClientRequestSubReason] = useState<string>('');
  const [selectedNonCooperationReasons, setSelectedNonCooperationReasons] = useState<NonCooperationReasonCode[]>([]);
  const [bulkExcludedClaims, setBulkExcludedClaims] = useState<Set<string>>(new Set());
  const [bulkConfirmData, setBulkConfirmData] = useState<{ claimIds: string[]; reason: string; clientName: string; clientEmail: string } | null>(null);

  // ID Request Modal state
  const [showIdRequestModal, setShowIdRequestModal] = useState(false);
  const [idRequestGroup, setIdRequestGroup] = useState<ClientGroup | null>(null);
  const [idRequestLoading, setIdRequestLoading] = useState(false);
  const [idRequestClientInfo, setIdRequestClientInfo] = useState<(IdRequestFields & {
    verificationStatus?: string | null;
    idRequestSent: boolean;
  }) | null>(null);

  // Side-effect-free charge preview for the bulk modal: lets the user see WHY
  // each claim is or isn't going to be charged while they're still deciding
  // which to keep/exclude. Keyed by claimId so the modal row render is O(1).
  const [bulkPreview, setBulkPreview] = useState<Record<string, CancelPreviewClaimEntry>>({});
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkPreviewError, setBulkPreviewError] = useState<string | null>(null);
  // #604: the preview's headline figures, kept so the fee modal can show the
  // real amount instead of a flat per-claim guess.
  const [bulkPreviewTotals, setBulkPreviewTotals] = useState<BulkPreviewTotals | null>(null);
  // Increment-on-open to discard stale responses if the user opens, closes,
  // and re-opens the modal faster than the network can respond.
  const bulkPreviewRequestId = useRef(0);

  // Cancellation reasons for the Cancel All modal
  const BULK_CANCELLATION_REASONS = [
    'Client request',
    'Cooling off period',
    'Non-cooperative client',
    'Lease agreement',
    'Duplicate claim',
    'Other Solicitor/CMC instructed',
    'Invalid lender',
    'General Closure (No Charge)',
    'General Closure (Charge)',
    'Other'
  ] as const;
  const REASON_GENERAL_CLOSURE = 'General Closure (No Charge)';
  const REASON_GENERAL_CLOSURE_CHARGE = 'General Closure (Charge)';
  const REASON_OTHER_SOLICITOR = 'Other Solicitor/CMC instructed';
  const REASON_INVALID_LENDER = 'Invalid lender';
  const REASON_LEASE_AGREEMENT = 'Lease agreement';
  const REASON_NON_COOPERATIVE = 'Non-cooperative client';

  // Load claims
  const loadClaims = useCallback(
    async (options: { silent?: boolean } = {}) => {
      const { silent = false } = options;
      if (!silent) setLoading(true);
      setError(null);

      try {
        const filters: ClaimFilters = {
          showAuditedOnly,
          showDeceasedOnly,
          statusFilter,
          sortBy,
          sortOrder
        };

        const startTime = Date.now();
        const data: ClaimPage & { totalCount?: number } = await fetchClaims(
          page,
          pageSize,
          search,
          filters
        );

        const loadTime = Date.now() - startTime;

        console.log(`Claims loaded in ${loadTime}ms`, {
          count: data.items.length,
          page,
          search,
          filters
        });

        setClaims(data.items as Claim[]);
        setHasMore(data.hasMore);
        setTotalCount(data.totalCount ?? totalCount ?? 0);
      } catch (err: any) {
        console.error('Error fetching claims:', err);
        setError(err.message || 'Error fetching claims. Please try again.');
        setClaims([]);
        setTotalCount(0);
        setHasMore(false);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [page, pageSize, search, totalCount, showAuditedOnly, showDeceasedOnly, statusFilter, sortBy, sortOrder]
  );

  // Group claims whenever claims change
  useEffect(() => {
    const groups = groupClaimsByClient(claims);
    // Preserve expanded state and fetch verification status
    const fetchVerificationStatuses = async () => {
      const groupsWithStatus = await Promise.all(groups.map(async (group) => {
        const expandedGroup = {
          ...group,
          expanded: expandedGroups.has(group.email)
        };
        
        // Fetch verification status if we have a clientId
        if (group.clientId) {
          try {
            const verification = await photoIdApi.getClientVerification(group.clientId);
            expandedGroup.idVerificationStatus = verification.verificationStatus;
          } catch (error) {
            console.error(`Failed to fetch verification status for client ${group.clientId}:`, error);
          }
        }
        
        return expandedGroup;
      }));
      
      setClientGroups(groupsWithStatus);
    };
    
    fetchVerificationStatuses();
  }, [claims, expandedGroups]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [showAuditedOnly, showDeceasedOnly, statusFilter, sortBy, sortOrder]);

  // Load claims on mount and when dependencies change
  useEffect(() => {
    loadClaims();
  }, [page, pageSize, search, showAuditedOnly, showDeceasedOnly, statusFilter, sortBy, sortOrder]);

  // Calculate statistics
  const stats = useMemo(() => calculateGroupStats(clientGroups), [clientGroups]);

  // Use the groups returned from the server without any client-side filtering
  const filteredGroups = clientGroups;

  // Calculate filtered statistics
  const filteredStats = useMemo(() => calculateGroupStats(filteredGroups), [filteredGroups]);


  // Handlers
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleToggleExpand = (email: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  const handleToggleAllExpand = () => {
    if (expandedGroups.size === filteredGroups.length) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(filteredGroups.map(g => g.email)));
    }
  };

  const handleSendLOC = async (claimId: string) => {
    setSendingIds(prev => new Set(prev).add(claimId));
    try {
      await sendLOC(claimId);
      await loadClaims({ silent: true }); // Reload to get updated data
    } catch (err: any) {
      console.error('Error sending LOC:', err);
      alert(err.response?.data?.error || 'Failed to send LOC');
    } finally {
      setSendingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(claimId);
        return newSet;
      });
    }
  };

  const handleCancelClaim = async (claimId: string, clientEmail?: string) => {
    setActionLoading(prev => new Set(prev).add(claimId));

    try {
      if (onCancelClaim) {
        await onCancelClaim(claimId, clientEmail);
        await loadClaims({ silent: true });
        return;
      }

      const reason = window.prompt('Please provide a reason for cancellation (optional):') || '';

      // Defined here (not above the onCancelClaim early return) so its
      // closure captures `reason` -- `reason` is block-scoped to this try,
      // declared just above.
      const showResult = (response: CancelClaimResponse) => {
        if (response.held) {
          const holdTaskId = heldReviewTaskId(response);
          if (holdTaskId && window.confirm(PHONE_REVIEW_QUESTION)) {
            setPhoneReviewTask({ taskId: holdTaskId, requestedReason: reason, clientEmail: clientEmail || null });
          } else {
            alert(heldCancellationAlert(response));
          }
          return;
        }
        if (response.requiresPayment) {
          alert(response.message || 'Payment request email has been sent to the client.');
        } else {
          alert(response.message || 'Cancellation completed successfully.');
        }
      };

      const attemptCancellation = async (payload: CancelClaimRequest) => {
        const response = await cancelClaim(claimId, payload);
        showResult(response.data);
        await loadClaims({ silent: true });
      };

      try {
        await attemptCancellation({ reason });
      } catch (err: any) {
        const status = err?.response?.status;
        const data = err?.response?.data;

        if (status === 409 && data?.paymentDecisionRequired) {
          const count = data.chargeableClaimCount ?? (data.chargeableClaimIds?.length ?? 1);
          const amount = typeof data.estimatedAmount === 'number' ? data.estimatedAmount : null;
          const promptMessage = [
            `Cancellation requires a payment decision for ${count} claim${count === 1 ? '' : 's'}.`,
            amount !== null ? `Estimated total fee: £${amount.toFixed(2)}.` : null,
            'Press OK to bypass payment (no fee).',
            'Press Cancel to request payment from the client.',
          ].filter(Boolean).join('\n');

          const bypass = window.confirm(promptMessage);

          await attemptCancellation({
            reason,
            paymentDecision: bypass ? 'bypass' : 'request',
            paymentDecisionConfirmed: true,
            bypassPayment: bypass,
          });
        } else {
          throw err;
        }
      }
    } catch (err: any) {
      console.error('Error cancelling claim:', err);
      alert(err?.response?.data?.error || err?.message || 'Failed to cancel claim');
    } finally {
      setActionLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(claimId);
        return newSet;
      });
    }
  };

  const handleSendLOCToAll = async (group: ClientGroup) => {
    const eligibleClaims = group.claims.filter(canSendFirstLoc);
    if (eligibleClaims.length === 0) return;

    const confirmed = window.confirm(
      `Send LOC to ${eligibleClaims.length} claims for ${group.clientName}?`
    );
    if (!confirmed) return;

    // Add all claim IDs to sending set
    eligibleClaims.forEach(c => setSendingIds(prev => new Set(prev).add(c.id)));

    let successCount = 0;
    let failCount = 0;

    for (const claim of eligibleClaims) {
      try {
        await sendLOC(claim.id);
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to send LOC for claim ${claim.id}:`, err);
      }
    }

    // Remove all claim IDs from sending set
    setSendingIds(prev => {
      const newSet = new Set(prev);
      eligibleClaims.forEach(c => newSet.delete(c.id));
      return newSet;
    });

    alert(`LOC sending complete:\n✓ Success: ${successCount}\n✗ Failed: ${failCount}`);
    await loadClaims({ silent: true });
  };

  const executeBulkCancellation = async ({
    group,
    claims,
    reason,
    nonCooperationReasonCodes,
    bypassPayment,
  }: {
    group: ClientGroup;
    claims: Claim[];
    reason: string;
    nonCooperationReasonCodes?: NonCooperationReasonCode[];
    bypassPayment: boolean | null;
  }) => {
    const handleSuccess = (response: CancelClaimResponse) => {
      if (response.held) {
        const holdTaskId = heldReviewTaskId(response);
        if (holdTaskId && window.confirm(PHONE_REVIEW_QUESTION)) {
          setPhoneReviewTask({ taskId: holdTaskId, requestedReason: reason, clientEmail: group.email || null });
        } else {
          alert(heldCancellationAlert(response));
        }
        return;
      }
      if (response.requiresPayment) {
        alert(response.message || `Payment request email has been sent to ${group.email}.`);
      } else {
        alert(response.message || `Successfully cancelled ${claims.length} claims for ${group.clientName}`);
      }
    };

    let completed = false;

    try {
      const attemptCancellation = async (payload: CancelClaimRequest) => {
        const response = await cancelClaim(claims[0].id, payload);
        handleSuccess(response.data);
      };

      try {
        // Send the exact set of selected claims. The backend cancels precisely these
        // (validated to the same client) so deselected claims are left untouched. This
        // covers both "cancel all" (full set) and a deselected subset, and keeps the
        // payment preview/decision scoped to the real selection.
        const initialPayload: CancelClaimRequest = {
          reason,
          claimIds: claims.map(c => c.id),
        };
        if (nonCooperationReasonCodes?.length) {
          initialPayload.nonCooperationReasonCodes = nonCooperationReasonCodes;
        }

        if (bypassPayment !== null) {
          initialPayload.paymentDecision = bypassPayment ? 'bypass' : 'request';
          initialPayload.paymentDecisionConfirmed = true;
          initialPayload.bypassPayment = bypassPayment;
        }

        await attemptCancellation(initialPayload);
        completed = true;
      } catch (err: any) {
        const status = err?.response?.status;
        const data = err?.response?.data;

        if (status === 409 && data?.paymentDecisionRequired) {
          // For General Closure (Charge), skip the bypass/require modal and always require payment
          if (reason === 'General Closure (Charge)') {
            await executeBulkCancellation({ group, claims, reason, nonCooperationReasonCodes, bypassPayment: false });
            return;
          }

          const preview: PaymentPreview = {
            chargeableClaimIds: data.chargeableClaimIds ?? [],
            chargeableClaimCount: data.chargeableClaimCount ?? (data.chargeableClaimIds?.length ?? claims.length),
            estimatedAmount: typeof data.estimatedAmount === 'number' ? data.estimatedAmount : undefined,
            currency: data.currency || 'GBP',
          };

          setPendingBulkCancellation({
            group,
            claims,
            reason,
            nonCooperationReasonCodes,
            requiresPaymentDecision: true,
            paymentPreview: preview,
          });
          setShowBypassModal(true);
          return;
        } else {
          throw err;
        }
      }
    } catch (err: any) {
      console.error('Failed to cancel claims:', err);
      const message = err?.response?.data?.error || err?.message || `Failed to cancel claims for ${group.clientName}`;
      alert(message);
    } finally {
      setActionLoading(prev => {
        const newSet = new Set(prev);
        claims.forEach(c => newSet.delete(c.id));
        return newSet;
      });
      if (completed) {
        await loadClaims({ silent: true });
        setPendingBulkCancellation(null);
        setShowBypassModal(false);
      }
    }
  };

  const handleBulkBypassDecision = async (bypassPayment: boolean) => {
    if (!pendingBulkCancellation) return;
    const { group, claims, reason, nonCooperationReasonCodes } = pendingBulkCancellation;
    setShowBypassModal(false);
    setPendingBulkCancellation(null);
    setBulkReasonDraft('');
    await executeBulkCancellation({
      group,
      claims,
      reason,
      nonCooperationReasonCodes,
      bypassPayment,
    });
  };

  const handleBulkBypassClose = () => {
    if (pendingBulkCancellation) {
      setActionLoading(prev => {
        const newSet = new Set(prev);
        pendingBulkCancellation.claims.forEach(c => newSet.delete(c.id));
        return newSet;
      });
    }
    setPendingBulkCancellation(null);
    setShowBypassModal(false);
    setBulkReasonDraft('');
    setSelectedNonCooperationReasons([]);
  };

  const handleBulkReasonCancel = () => {
    if (pendingBulkCancellation) {
      setActionLoading(prev => {
        const newSet = new Set(prev);
        pendingBulkCancellation.claims.forEach(c => newSet.delete(c.id));
        return newSet;
      });
    }
    setPendingBulkCancellation(null);
    setBulkReasonDraft('');
    setBulkSelectedReason('Client request');
    setBulkClientRequestSubReason('');
    setSelectedNonCooperationReasons([]);
    setBulkExcludedClaims(new Set());
    setShowBulkReasonModal(false);
  };

  const handleBulkReasonSubmit = async () => {
    if (!pendingBulkCancellation) return;

    // Determine final reason
    let finalReason: string;
    if (bulkSelectedReason === 'Other') {
      finalReason = bulkReasonDraft.trim() ? bulkReasonDraft.trim() : 'Admin cancelled via UI';
    } else if (bulkSelectedReason === REASON_CLIENT_REQUEST) {
      if (!bulkClientRequestSubReason) {
        alert('Select a Client request sub-category.');
        return;
      }
      finalReason = buildClientRequestReason(bulkClientRequestSubReason);
    } else {
      finalReason = bulkSelectedReason;
    }

    if (finalReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0) {
      alert('Select at least one outstanding client requirement.');
      return;
    }

    // Filter out excluded claims
    const claimsToCancel = pendingBulkCancellation.claims.filter(c => !bulkExcludedClaims.has(c.id));
    if (claimsToCancel.length === 0) {
      alert('No claims selected for cancellation.');
      return;
    }

    const updated = {
      ...pendingBulkCancellation,
      claims: claimsToCancel,
      reason: finalReason,
      nonCooperationReasonCodes: finalReason === REASON_NON_COOPERATIVE
        ? selectedNonCooperationReasons
        : undefined,
    };
    setPendingBulkCancellation(updated);
    setShowBulkReasonModal(false);

    // For "Other Solicitor/CMC instructed" or "General Closure (Charge)" — confirm the
    // fee before proceeding. The figure comes from the side-effect-free preview
    // fetched when the modal opened (Freshdesk #604) — never a flat per-claim guess.
    if (finalReason === REASON_OTHER_SOLICITOR || finalReason === REASON_GENERAL_CLOSURE_CHARGE) {
      setBulkConfirmData({
        claimIds: claimsToCancel.map(c => c.id),
        reason: finalReason,
        clientName: pendingBulkCancellation.group.clientName,
        clientEmail: pendingBulkCancellation.group.email,
      });
      setShowBulkConfirmModal(true);
      return;
    }

    // Add loading state for selected claims
    claimsToCancel.forEach(c => setActionLoading(prev => new Set(prev).add(c.id)));
    setBulkReasonDraft('');
    setBulkSelectedReason('Client request');
    setBulkClientRequestSubReason('');
    setSelectedNonCooperationReasons([]);
    setBulkExcludedClaims(new Set());

    // Fee-free closure reasons bypass payment.
    if (finalReason === REASON_GENERAL_CLOSURE || finalReason === REASON_LEASE_AGREEMENT || finalReason === REASON_INVALID_LENDER) {
      await executeBulkCancellation({
        group: updated.group,
        claims: updated.claims,
        reason: updated.reason,
        nonCooperationReasonCodes: updated.nonCooperationReasonCodes,
        bypassPayment: true,
      });
    } else {
      await executeBulkCancellation({
        group: updated.group,
        claims: updated.claims,
        reason: updated.reason,
        nonCooperationReasonCodes: updated.nonCooperationReasonCodes,
        bypassPayment: null,
      });
    }
  };

  const handleBulkConfirmProceed = async () => {
    if (!pendingBulkCancellation) return;

    setShowBulkConfirmModal(false);
    setBulkConfirmData(null);

    // Add loading state
    pendingBulkCancellation.claims.forEach(c => setActionLoading(prev => new Set(prev).add(c.id)));
    setBulkReasonDraft('');
    setBulkSelectedReason('Client request');
    setBulkClientRequestSubReason('');
    setSelectedNonCooperationReasons([]);
    setBulkExcludedClaims(new Set());

    await executeBulkCancellation({
      group: pendingBulkCancellation.group,
      claims: pendingBulkCancellation.claims,
      reason: pendingBulkCancellation.reason,
      nonCooperationReasonCodes: pendingBulkCancellation.nonCooperationReasonCodes,
      bypassPayment: null,
    });
  };

  const handleBulkConfirmCancel = () => {
    setShowBulkConfirmModal(false);
    setBulkConfirmData(null);
    if (pendingBulkCancellation) {
      setActionLoading(prev => {
        const newSet = new Set(prev);
        pendingBulkCancellation.claims.forEach(c => newSet.delete(c.id));
        return newSet;
      });
    }
    setPendingBulkCancellation(null);
    setBulkReasonDraft('');
    setBulkSelectedReason('Client request');
    setBulkClientRequestSubReason('');
    setSelectedNonCooperationReasons([]);
    setBulkExcludedClaims(new Set());
  };

  const handleCancelAll = async (group: ClientGroup) => {
    const activeClaims = group.claims.filter(c => c.status !== 'CANCELLED');
    if (activeClaims.length === 0) return;

    // Open modal with all claims pre-selected
    setBulkExcludedClaims(new Set());
    setBulkSelectedReason('Client request');
    setBulkClientRequestSubReason('');
    setSelectedNonCooperationReasons([]);
    setBulkReasonDraft('');
    setPendingBulkCancellation({
      group,
      claims: activeClaims,
      reason: '',
      requiresPaymentDecision: false,
    });
    setShowBulkReasonModal(true);

    // Kick off the side-effect-free charge preview so the modal can show CHARGE/FREE
    // + reason per row. Backend treats the endpoint as cancelAllAssociated=true →
    // returns the full per-claim breakdown for this client. We key by the first
    // active claim because /cancel/preview is per-claim-id in the URL.
    setBulkPreview({});
    setBulkPreviewTotals(null);
    setBulkPreviewError(null);
    setBulkPreviewLoading(true);
    const myReqId = ++bulkPreviewRequestId.current;
    try {
      const preview = await fetchCancelPreview(activeClaims[0].id, true);
      if (myReqId !== bulkPreviewRequestId.current) return; // user closed/reopened — drop
      const byId: Record<string, CancelPreviewClaimEntry> = {};
      for (const entry of preview.claimBreakdown) byId[entry.claimId] = entry;
      setBulkPreview(byId);
      setBulkPreviewTotals({
        chargeableCount: preview.chargeableCount ?? 0,
        estimatedAmount: preview.estimatedAmount ?? 0,
      });
    } catch (err) {
      if (myReqId !== bulkPreviewRequestId.current) return;
      console.error('Failed to fetch bulk cancel preview:', err);
      setBulkPreviewError('Could not load charge breakdown — cancel proceeds via the existing payment safety-net.');
    } finally {
      if (myReqId === bulkPreviewRequestId.current) setBulkPreviewLoading(false);
    }
  };

  const handleReinstateClaim = async (
    claimId: string,
    force = false,
    remainingPaymentAction?: RemainingPaymentAction,
  ) => {
    setActionLoading(prev => new Set(prev).add(claimId));
    if (remainingPaymentAction) setReinstatementPaymentSubmitting(true);
    try {
      if (!remainingPaymentAction) {
        const impact = await fetchReinstatementPaymentImpact(claimId);
        if (impact.blocked) {
          alert(impact.blockReason || 'Finance must review this payment before reinstatement.');
          return;
        }
        if (impact.requiresDecision) {
          setReinstatementPaymentPrompt({ impact, force });
          return;
        }
        if (!force) {
          const confirmed = window.confirm(
            `Are you sure you want to reinstate claim ${claimId}? This will clear the LOC sent date so it can be automatically resent.`
          );
          if (!confirmed) return;
        }
      }

      setReinstatementPaymentPrompt(null);
      const updatedClaimData = await reinstateClaim(claimId, force, remainingPaymentAction);
      await loadClaims({ silent: true });
      const paymentResult = (updatedClaimData as {
        reinstatementPaymentResult?: ReinstatementPaymentResult;
      } | undefined)?.reinstatementPaymentResult;
      if (paymentResult?.replacementIsManual) {
        if (paymentResult.replacementPaymentUrl) {
          navigator.clipboard?.writeText(paymentResult.replacementPaymentUrl).catch(() => undefined);
        }
        alert(`Claim ${claimId} reinstated.${reinstatementPaymentMessage(paymentResult)}`);
      }
    } catch (err: any) {
      // The lender for this claim is non-viable (closed). Both admins and normal
      // users get the in-app modal; admins can override ("Reinstate anyway"),
      // non-admins see a read-only "ask an admin" message with only a Close
      // button (no override action). The backend enforces admin-only override
      // regardless, so hiding the button is UX, not the security boundary.
      if (err.response?.status === 409 && err.response?.data?.lenderDisabled && !force) {
        const lenderName = err.response.data.lenderName || 'this lender';
        setLenderBlockModal({ claimId, lenderName });
        return;
      }
      console.error('Error reinstating claim:', err);
      alert(err.response?.data?.error || 'Failed to reinstate claim');
    } finally {
      setReinstatementPaymentSubmitting(false);
      setActionLoading(prev => {
        const s = new Set(prev);
        s.delete(claimId);
        return s;
      });
    }
  };

  const handleRequestId = async (claim: Claim) => {
    // Get client ID from claim
    const clientId = claim.clientId;
    if (!clientId) {
      alert('This claim does not have an associated client ID');
      return;
    }

    const confirmed = window.confirm(
      `Send photo ID verification request to ${claim.firstName} ${claim.lastName} (${claim.email})?`
    );
    if (!confirmed) return;

    setActionLoading(prev => new Set(prev).add(claim.id));
    try {
      await sendIdRequest([clientId]);
      alert('Photo ID request sent successfully');
    } catch (err: any) {
      console.error('Error sending ID request:', err);
      alert(err.response?.data?.error || 'Failed to send ID request');
    } finally {
      setActionLoading(prev => {
        const s = new Set(prev);
        s.delete(claim.id);
        return s;
      });
    }
  };

  // Open ID request modal for a client group
  const handleOpenIdRequestModal = async (group: ClientGroup) => {
    setIdRequestGroup(group);
    setShowIdRequestModal(true);
    setIdRequestClientInfo(null);

    // Fetch client verification info
    const clientId = group.clientId || group.claims[0]?.clientId;
    if (clientId) {
      try {
        const info = await photoIdApi.getClientVerification(clientId);
        setIdRequestClientInfo({
          verificationStatus: info.verificationStatus,
          idRequestSent: info.idRequestSent,
          idRequestSentDate: info.idRequestSentDate,
          idRequestLastSentDate: info.idRequestLastSentDate,
          idRequestReminderCount: info.idRequestReminderCount,
          idRequestPause: info.idRequestPause,
          clientDeceased: info.clientDeceased,
        });
      } catch (err) {
        console.error('Error fetching client verification info:', err);
        setIdRequestClientInfo({
          verificationStatus: group.idVerificationStatus,
          idRequestSent: false
        });
      }
    }
  };

  // Confirm and send ID request
  const handleConfirmIdRequest = async () => {
    if (!idRequestGroup) return;

    const clientId = idRequestGroup.clientId || idRequestGroup.claims[0]?.clientId;
    if (!clientId) {
      alert('Could not determine client ID');
      return;
    }

    setIdRequestLoading(true);
    try {
      await sendIdRequest([clientId]);
      alert('Photo ID request sent successfully');
      setShowIdRequestModal(false);
      setIdRequestGroup(null);
      setIdRequestClientInfo(null);
    } catch (err: any) {
      console.error('Error sending ID request:', err);
      alert(err.response?.data?.error || 'Failed to send ID request');
    } finally {
      setIdRequestLoading(false);
    }
  };

  const handleClaimAction = (action: string, claim: Claim) => {
    switch (action) {
      case 'details':
        onSelectClaim(claim);
        break;
      case 'sendLOC':
        handleSendLOC(claim.id);
        break;
      case 'cancel':
        handleCancelClaim(claim.id, claim.email);
        break;
      case 'reinstate':
        handleReinstateClaim(claim.id);
        break;
      case 'docs':
        onShowDocs(claim);
        break;
      case 'requestId':
        handleRequestId(claim);
        break;
    }
  };

  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;

  // #604: the fee the confirm modal shows, derived live from the preview so a
  // response that lands after the modal opens still fills it in.
  const bulkConfirmFee = bulkConfirmData
    ? summariseBulkFee({
        selectedClaimIds: bulkConfirmData.claimIds,
        breakdownById: bulkPreview,
        totals: bulkPreviewTotals,
        loading: bulkPreviewLoading,
        failed: bulkPreviewError !== null,
      })
    : null;

  return (
    <div className="hierarchical-claims-container">
      <div className="hierarchical-header">
        <form onSubmit={handleSearch} style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <input
                type="text"
                placeholder="Search by ID, name, email, phone, postcode, agreement number, lender reference, legacy claim/client ID, litigation friend name/email..."
                value={searchInput}
                onChange={handleSearchInputChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              {search && (
                <div style={{ 
                  marginTop: '5px', 
                  fontSize: '12px', 
                  color: '#666' 
                }}>
                  Searching for: <strong>"{search}"</strong> • Found {totalCount} result{totalCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <button type="submit" disabled={loading} style={{
              padding: '8px 16px',
              backgroundColor: '#01B8C0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              {loading ? 'Searching...' : 'Search'}
            </button>
            {search && (
              <button type="button" onClick={handleClearSearch} style={{
                padding: '8px 16px',
                backgroundColor: '#ff6b6b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                ✕ Clear
              </button>
            )}
          </div>
        </form>

        {/* Search tips */}
        {!search && searchInput.length === 0 && (
          <div style={{ 
            marginBottom: '10px', 
            padding: '8px 12px',
            backgroundColor: '#f0f9ff',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#0369a1',
            border: '1px solid #bae6fd'
          }}>
            💡 <strong>Search tips:</strong> You can search by full names (e.g., "John Smith"), email addresses, postcodes (with or without spaces), phone numbers, claim IDs, agreement numbers, lender reference numbers, or legacy claim/client IDs. All searches are case-insensitive.
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'nowrap', alignItems: 'center', justifyContent: "center" }}>
          <button className="expand-all-btn" onClick={handleToggleAllExpand}>
            {expandedGroups.size === filteredGroups.length ? '➖' : '➕'}
            {expandedGroups.size === filteredGroups.length ? 'Collapse All' : 'Expand All'}
          </button>
          
          {/* Filters */}
          <div className="filter-controls">
            {/* Audit Filter */}
            <label className="filter-label">
              <input
                type="checkbox"
                checked={showAuditedOnly}
                onChange={(e) => setShowAuditedOnly(e.target.checked)}
              />
              Show audited only
            </label>

            {/* Deceased/Litigation Friend Filter */}
            <label className="filter-label">
              <input
                type="checkbox"
                checked={showDeceasedOnly}
                onChange={(e) => setShowDeceasedOnly(e.target.checked)}
              />
              Show Deceased Claimants Only
            </label>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="needsLoc">Needs LOC</option>
              <option value="locSent">LOC Sent</option>
              <option value="bulkSubmitted">Bulk Submitted</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
            
            {/* Sort Options */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'date')}
              className="filter-select"
            >
              <option value="date">Sort by Date</option>
              <option value="name" title="Note: Name sorting may be slower for large datasets">
                Sort by Name {totalCount > 1000 && '⚠️'}
              </option>
            </select>
            
            {/* Sort Order */}
            <button
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="sort-button"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
              {sortBy === 'name' ? (sortOrder === 'asc' ? 'A-Z' : 'Z-A') : (sortOrder === 'asc' ? 'Oldest' : 'Newest')}
            </button>
            
            {/* Clear Filters */}
            {(showAuditedOnly || showDeceasedOnly || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setShowAuditedOnly(false);
                  setShowDeceasedOnly(false);
                  setStatusFilter('all');
                }}
                className="sort-button"
                style={{ background: '#ff6b6b', color: 'white', border: '1px solid #ff5252' }}
              >
                ✕ Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">Clients:</span>
          <span className="stat-value">
            {filteredStats.totalClients}
            {filteredStats.totalClients !== stats.totalClients && (
              <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>
                of {stats.totalClients}
              </span>
            )}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Total Claims:</span>
          <span className="stat-value">
            {filteredStats.totalClaims}
            {filteredStats.totalClaims !== stats.totalClaims && (
              <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>
                of {stats.totalClaims}
              </span>
            )}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Active:</span>
          <span className="stat-value">{filteredStats.totalActive}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Cancelled:</span>
          <span className="stat-value">{filteredStats.totalCancelled}</span>
        </div>
        {(showAuditedOnly || showDeceasedOnly || statusFilter !== 'all' || searchInput) && (
          <div className="stat-item" style={{ background: '#fff3cd', padding: '8px 12px', borderRadius: '4px' }}>
            <span style={{ fontSize: '13px', color: '#856404' }}>
              🔍 Filtered view
            </span>
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '20px', color: 'red' }}>
          Error: {error}
        </div>
      )}

      {loading ? (
        <div className="loading-container" style={{ 
          padding: '60px 20px', 
          textAlign: 'center',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #01B8C0',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          <p style={{ fontSize: '16px', color: '#666' }}>Loading claims...</p>
          {search && <p style={{ fontSize: '14px', color: '#999', marginTop: '10px' }}>Searching for "{search}"...</p>}
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="empty-state">
          <h3>No claims found</h3>
          <p>Try adjusting your search criteria or clear the search.</p>
        </div>
      ) : (
        <>
          {!isMobile ? (
            <table className="client-groups-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Claims</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map(group => (
                  <ClientGroupRow
                    key={group.email}
                    group={group}
                    onToggleExpand={handleToggleExpand}
                    onSendLOCToAll={handleSendLOCToAll}
                    onCancelAll={handleCancelAll}
                    onRequestId={handleOpenIdRequestModal}
                    onClaimAction={handleClaimAction}
                    sendingIds={sendingIds}
                    actionLoading={actionLoading}
                  />
                ))}
              </tbody>
            </table>
          ) : (
            <div className="mobile-client-groups">
              {filteredGroups.map(group => (
                <ClientGroupRow
                  key={group.email}
                  group={group}
                  onToggleExpand={handleToggleExpand}
                  onSendLOCToAll={handleSendLOCToAll}
                  onCancelAll={handleCancelAll}
                  onRequestId={handleOpenIdRequestModal}
                  onClaimAction={handleClaimAction}
                  sendingIds={sendingIds}
                  actionLoading={actionLoading}
                  isMobile={true}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className="pagination-container">
          <div className="pagination">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1 || loading}
              className="page-btn"
            >
              ««
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="page-btn"
            >
              ‹
            </button>
            
            <span className="page-info">
              Page {page} of {Math.ceil(totalCount / pageSize)}
            </span>
            
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore || loading}
              className="page-btn"
            >
              ›
            </button>
            <button
              onClick={() => setPage(Math.ceil(totalCount / pageSize))}
              disabled={!hasMore || page === Math.ceil(totalCount / pageSize) || loading}
              className="page-btn"
            >
              »»
            </button>
          </div>
          <div className="page-size-selector">
            <span>Show: </span>
            <select 
              value={pageSize} 
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setPage(1);
              }}
              className="page-size-select"
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
            <span> items</span>
          </div>
        </div>
      )}

      {showBulkReasonModal && pendingBulkCancellation && (
        <Modal onClose={handleBulkReasonCancel}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{ padding: '24px', maxWidth: '640px', width: '100%' }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#111827' }}>Cancel All Claims</h3>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                Are you sure you want to cancel claims for <strong>{pendingBulkCancellation.group.clientName}</strong>?
              </p>

              {bulkPreviewError && (
                <div style={{
                  padding: '8px 12px',
                  marginBottom: '12px',
                  background: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '6px',
                  color: '#92400e',
                  fontSize: '12px',
                }}>
                  {bulkPreviewError}
                </div>
              )}

              {/* Claims list with checkboxes + per-claim CHARGE/FREE breakdown */}
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                marginBottom: '16px',
                maxHeight: '280px',
                overflowY: 'auto'
              }}>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                }}>
                  <span style={{ width: '24px' }}></span>
                  <span style={{ flex: 1 }}>Claim ID</span>
                  <span style={{ flex: 1 }}>Lender</span>
                  <span style={{ width: '96px' }}>Charge?</span>
                  <span style={{ width: '70px' }}>Status</span>
                </div>
                {pendingBulkCancellation.claims.map(claim => {
                  const preview = bulkPreview[claim.id];
                  const isRingfenced = preview?.reasonCode === 'FREE_AGE_RINGFENCED';
                  const chargeBadge = preview
                    ? (isRingfenced
                        // Reuses the amber charge palette (not the grey free one) --
                        // a fee may well follow the 70+ review call.
                        ? { label: '70+ REVIEW', bg: '#fde68a', fg: '#78350f', border: '1px solid #b45309' }
                        : preview.willCharge
                          ? { label: 'CHARGE', bg: '#fef3c7', fg: '#92400e', border: 'none' }
                          : { label: 'FREE',   bg: '#e2e8f0', fg: '#475569', border: 'none' })
                    : (bulkPreviewLoading
                        ? { label: '…',     bg: '#f1f5f9', fg: '#94a3b8', border: 'none' }
                        : { label: '—',     bg: '#f1f5f9', fg: '#94a3b8', border: 'none' });
                  return (
                    <label
                      key={claim.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '8px 12px',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        backgroundColor: bulkExcludedClaims.has(claim.id) ? '#f9fafb' : '#fff',
                        opacity: bulkExcludedClaims.has(claim.id) ? 0.5 : 1,
                        fontSize: '13px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                          type="checkbox"
                          checked={!bulkExcludedClaims.has(claim.id)}
                          disabled={bulkSelectedReason === REASON_NON_COOPERATIVE}
                          onChange={() => {
                            setBulkExcludedClaims(prev => {
                              const next = new Set(prev);
                              if (next.has(claim.id)) {
                                next.delete(claim.id);
                              } else {
                                next.add(claim.id);
                              }
                              return next;
                            });
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            margin: 0,
                            cursor: bulkSelectedReason === REASON_NON_COOPERATIVE ? 'not-allowed' : 'pointer',
                          }}
                        />
                        <span style={{ flex: 1, fontWeight: 500 }}>{claim.id}</span>
                        <span style={{ flex: 1, color: '#6b7280' }}>{claim.craFinanceLender || '—'}</span>
                        <span
                          aria-label={
                            preview
                              ? isRingfenced
                                ? `${claim.id}: fee held for over-70 review`
                                : `${claim.id}: ${preview.willCharge ? 'will be charged' : 'free to cancel'}`
                              : undefined
                          }
                          style={{
                            width: '96px',
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: '999px',
                            backgroundColor: chargeBadge.bg,
                            color: chargeBadge.fg,
                            border: chargeBadge.border,
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {chargeBadge.label}
                        </span>
                        <span style={{
                          width: '70px',
                          fontSize: '11px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          textAlign: 'center'
                        }}>Active</span>
                      </div>
                      {preview?.reasonText && (
                        <div style={{
                          marginLeft: '28px',
                          marginTop: '4px',
                          fontSize: '11px',
                          color: '#64748b',
                          fontStyle: 'italic',
                        }}>
                          {preview.reasonText}
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>

              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280' }}>
                {pendingBulkCancellation.claims.length - bulkExcludedClaims.size} of {pendingBulkCancellation.claims.length} claims selected
              </p>

              {/* Cancellation Reason Dropdown */}
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="bulk-cancel-reason" style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                  Cancellation Reason
                </label>
                <select
                  id="bulk-cancel-reason"
                  value={bulkSelectedReason}
                  onChange={(e) => {
                    const nextReason = e.target.value;
                    setBulkSelectedReason(nextReason);
                    setBulkClientRequestSubReason('');
                    if (nextReason === REASON_NON_COOPERATIVE) {
                      setBulkExcludedClaims(new Set());
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    boxShadow: 'inset 0 1px 2px rgba(148, 163, 184, 0.25)'
                  }}
                >
                  {BULK_CANCELLATION_REASONS.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {bulkSelectedReason === REASON_CLIENT_REQUEST && (
                <ClientRequestSubReasonSelect
                  selected={bulkClientRequestSubReason}
                  onChange={setBulkClientRequestSubReason}
                  borderColor="#cbd5e1"
                />
              )}

              {/* Info notices for special reasons */}
              {bulkSelectedReason === REASON_OTHER_SOLICITOR && (
                <div style={{
                  backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px',
                  padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#92400e'
                }}>
                  <strong>Note:</strong> The client will be charged the standard termination fee. No reinstatement link will be included. A single payment link will be sent for all claims.
                </div>
              )}

              {bulkSelectedReason === REASON_GENERAL_CLOSURE && (
                <div style={{
                  backgroundColor: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '8px',
                  padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#1e40af'
                }}>
                  <strong>Note:</strong> General closure does not require payment and will not include a reinstatement link.
                </div>
              )}

              {bulkSelectedReason === REASON_INVALID_LENDER && (
                <div style={{
                  backgroundColor: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '8px',
                  padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#1e40af'
                }}>
                  <strong>Note:</strong> Claims will close without charge using the invalid-lender client email. No lender withdrawal or reinstatement link will be sent.
                </div>
              )}

              {bulkSelectedReason === REASON_LEASE_AGREEMENT && (
                <div style={{
                  backgroundColor: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '8px',
                  padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#1e40af'
                }}>
                  <strong>Note:</strong> Lease agreement cancellations do not require payment and will not include a reinstatement link.
                </div>
              )}

              {bulkSelectedReason === REASON_NON_COOPERATIVE && (
                <>
                  <div style={{
                    backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px',
                    padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#92400e'
                  }}>
                    <strong>Note:</strong> All live claims must remain selected. The closure email will contain only the requirements selected below and will not include a reinstatement link.
                  </div>
                  <NonCooperationReasonsFieldset
                    selected={selectedNonCooperationReasons}
                    onChange={setSelectedNonCooperationReasons}
                  />
                </>
              )}

              {bulkSelectedReason === REASON_GENERAL_CLOSURE_CHARGE && (
                <div style={{
                  backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px',
                  padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#92400e'
                }}>
                  <strong>Note:</strong> The client will be charged the standard termination fee. A payment link will be sent separately.
                </div>
              )}

              {/* Custom reason textarea when "Other" is selected */}
              {bulkSelectedReason === 'Other' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    Custom reason
                  </label>
                  <textarea
                    value={bulkReasonDraft}
                    onChange={(e) => setBulkReasonDraft(e.target.value)}
                    rows={3}
                    placeholder="Enter custom cancellation reason..."
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      padding: '12px',
                      fontSize: '14px',
                      lineHeight: 1.5,
                      resize: 'vertical',
                      boxShadow: 'inset 0 1px 2px rgba(148, 163, 184, 0.25)',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleBulkReasonCancel}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkReasonSubmit}
                  disabled={
                    pendingBulkCancellation.claims.length - bulkExcludedClaims.size === 0
                    || (bulkSelectedReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0)
                    || isClientRequestSubReasonMissing(bulkSelectedReason, bulkClientRequestSubReason)
                  }
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: (
                      pendingBulkCancellation.claims.length - bulkExcludedClaims.size === 0
                      || (bulkSelectedReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0)
                      || isClientRequestSubReasonMissing(bulkSelectedReason, bulkClientRequestSubReason)
                    ) ? '#94a3b8' : '#dc2626',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: (
                      pendingBulkCancellation.claims.length - bulkExcludedClaims.size === 0
                      || (bulkSelectedReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0)
                      || isClientRequestSubReasonMissing(bulkSelectedReason, bulkClientRequestSubReason)
                    ) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
                  }}
                >
                  Cancel {pendingBulkCancellation.claims.length - bulkExcludedClaims.size} Claim{pendingBulkCancellation.claims.length - bulkExcludedClaims.size === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {phoneReviewTask && (
        <HoldReviewModal
          taskId={phoneReviewTask.taskId}
          requestedReason={phoneReviewTask.requestedReason}
          clientEmail={phoneReviewTask.clientEmail}
          onClose={() => setPhoneReviewTask(null)}
          onCompleted={async () => {
            setPhoneReviewTask(null);
            await loadClaims({ silent: true });
          }}
        />
      )}

      {showBypassModal && pendingBulkCancellation && (
        <Modal onClose={handleBulkBypassClose}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{ padding: '18px', maxWidth: '420px', width: '100%' }}>
            <h3 style={{ margin: '0 0 12px 0' }}>Payment Handling</h3>
            <p style={{ margin: '0 0 20px 0', color: '#4b5563' }}>
              How would you like to handle the cancellation fee for <strong>{pendingBulkCancellation.group.clientName}</strong>?
            </p>
            {pendingBulkCancellation.paymentPreview && (
              <div
                style={{
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #cbd5f5',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#334155'
                }}
              >
                <p style={{ margin: 0 }}>
                  <strong>{pendingBulkCancellation.paymentPreview.chargeableClaimCount}</strong> claim
                  {pendingBulkCancellation.paymentPreview.chargeableClaimCount === 1 ? '' : 's'} meet the fee criteria.
                  {typeof pendingBulkCancellation.paymentPreview.estimatedAmount === 'number' && (
                    <> Estimated total: <strong>
                      {pendingBulkCancellation.paymentPreview.currency === 'GBP' ? '£' : `${pendingBulkCancellation.paymentPreview.currency || ''} `}
                      {pendingBulkCancellation.paymentPreview.estimatedAmount.toFixed(2)}
                    </strong>.</>
                  )}
                </p>
                {pendingBulkCancellation.paymentPreview.chargeableClaimIds.length > 0 && (
                  <p style={{ margin: '8px 0 0 0', color: '#475569' }}>
                    Claims: {pendingBulkCancellation.paymentPreview.chargeableClaimIds.join(', ')}
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={() => handleBulkBypassDecision(true)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Bypass payment (free cancellation)
              </button>
              <button
                type="button"
                onClick={() => handleBulkBypassDecision(false)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Require payment (send Stripe link)
              </button>
              <button
                type="button"
                onClick={handleBulkBypassClose}
                style={{
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
            </div>
          </div>
        </Modal>
      )}

   {/* Cost confirmation modal for Other Solicitor/CMC and General Closure (Charge) — #604 */}
   {showBulkConfirmModal && bulkConfirmData && bulkConfirmFee && (
     <Modal onClose={handleBulkConfirmCancel}>
       <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
         <div style={{ padding: '24px', maxWidth: '480px', width: '100%' }}>
           <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#111827' }}>Confirm Cancellation Fee</h3>

           <div style={{
             backgroundColor: '#f8fafc',
             border: '1px solid #e2e8f0',
             borderRadius: '8px',
             padding: '16px',
             marginBottom: '20px'
           }}>
             <div style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
               <strong>Client:</strong> {bulkConfirmData.clientName}
             </div>
             <div style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
               <strong>Claims to cancel:</strong> {bulkConfirmData.claimIds.length}
             </div>
             {bulkConfirmFee.status === 'ready' && (
               <>
                 <div style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
                   <strong>Claims charged:</strong> {bulkConfirmFee.chargedCount}
                 </div>
                 {bulkConfirmFee.ringfencedCount > 0 && (
                   <div style={{ marginBottom: '12px', fontSize: '14px', color: '#92400e', fontWeight: 600 }}>
                     {`Held for 70+ review: ${bulkConfirmFee.ringfencedCount} claim${bulkConfirmFee.ringfencedCount === 1 ? '' : 's'} — fee decided on the call`}
                   </div>
                 )}
                 <div style={{
                   fontSize: '20px',
                   fontWeight: 700,
                   color: '#111827',
                   borderTop: '1px solid #e2e8f0',
                   paddingTop: '12px',
                   marginTop: '4px'
                 }}>
                   Total: {bulkConfirmFee.chargedCount > 0 ? formatPounds(bulkConfirmFee.totalPounds) : 'No fee'}
                 </div>
               </>
             )}
             {bulkConfirmFee.status === 'loading' && (
               <div style={{ fontSize: '14px', color: '#475569' }}>Working out the fee…</div>
             )}
             {bulkConfirmFee.status === 'unavailable' && (
               <div role="status" style={{ fontSize: '14px', color: '#92400e' }}>
                 {"The fee couldn't be worked out here. Only chargeable claims will be charged, and the client's email will show the exact amount."}
               </div>
             )}
           </div>

           {bulkConfirmFee.status === 'ready' && bulkConfirmFee.chargedCount > 0 && (
             <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6b7280' }}>
               A payment link for <strong>{formatPounds(bulkConfirmFee.totalPounds)}</strong> will be sent to <strong>{bulkConfirmData.clientEmail}</strong>.
             </p>
           )}
           {bulkConfirmFee.status === 'ready' && bulkConfirmFee.chargedCount === 0 && (
             <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6b7280' }}>
               No fee will be charged now, so no payment link will be sent.
             </p>
           )}

           <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
             <button
               type="button"
               onClick={handleBulkConfirmCancel}
               style={{
                 padding: '10px 18px',
                 borderRadius: '8px',
                 border: '1px solid #d1d5db',
                 backgroundColor: '#f8fafc',
                 color: '#475569',
                 cursor: 'pointer',
                 fontSize: '14px'
               }}
             >
               Cancel
             </button>
             <button
               type="button"
               onClick={handleBulkConfirmProceed}
               disabled={bulkConfirmFee.status === 'loading'}
               style={{
                 padding: '10px 18px',
                 borderRadius: '8px',
                 border: 'none',
                 backgroundColor: '#dc2626',
                 color: '#fff',
                 fontWeight: 600,
                 cursor: bulkConfirmFee.status === 'loading' ? 'not-allowed' : 'pointer',
                 opacity: bulkConfirmFee.status === 'loading' ? 0.6 : 1,
                 fontSize: '14px',
                 boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
               }}
             >
               {bulkConfirmFee.status === 'ready' && bulkConfirmFee.chargedCount > 0
                 ? 'Confirm & Send Payment Link'
                 : 'Confirm cancellation'}
             </button>
           </div>
         </div>
       </div>
     </Modal>
   )}

      {/* Non-viable (disabled) lender — admin override to reinstate */}
      {lenderBlockModal && (
        <Modal onClose={() => setLenderBlockModal(null)}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{ padding: '24px', maxWidth: '480px', width: '100%' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#dc2626' }}>Lender is non-viable</h3>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#475569' }}>
                Lender <strong>{lenderBlockModal.lenderName}</strong> is non-viable (closed), so reinstating claim <strong>{lenderBlockModal.claimId}</strong> is normally blocked.
              </p>
              <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6b7280' }}>
                {isAdmin
                  ? 'As an admin you can override this and reinstate anyway.'
                  : 'You do not have permission to override this. Please escalate to an admin to reinstate.'}
              </p>
              {/* Normal users get no action buttons (they can dismiss via the modal close).
                  Only admins see Cancel + Reinstate anyway. */}
              {isAdmin && (
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setLenderBlockModal(null)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const { claimId } = lenderBlockModal;
                      setLenderBlockModal(null);
                      handleReinstateClaim(claimId, true);
                    }}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#dc2626',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
                    }}
                  >
                    Reinstate anyway
                  </button>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {reinstatementPaymentPrompt && (
        <ReinstatementPaymentModal
          impact={reinstatementPaymentPrompt.impact}
          submitting={reinstatementPaymentSubmitting}
          onClose={() => setReinstatementPaymentPrompt(null)}
          onChoose={(action) => {
            void handleReinstateClaim(
              reinstatementPaymentPrompt.impact.claimId,
              reinstatementPaymentPrompt.force,
              action,
            );
          }}
        />
      )}

      {/* ID Request Confirmation Modal - shows claim details */}
      {showIdRequestModal && idRequestGroup && (
        <Modal onClose={() => {
          setShowIdRequestModal(false);
          setIdRequestGroup(null);
          setIdRequestClientInfo(null);
        }}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{ padding: '24px', maxWidth: '480px', width: '100%' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#111827' }}>
                Request Photo ID
              </h3>

              <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
                  <strong>Client:</strong> {idRequestGroup.clientName}
                </div>
                <div style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
                  <strong>Email:</strong> {idRequestGroup.email}
                </div>
                <div style={{ marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
                  <strong>Active Claims:</strong> {idRequestGroup.activeClaims}
                </div>

                <div style={{ marginBottom: '12px', fontSize: '13px', color: '#475569' }}>
                  {idRequestGroup.claims
                    .filter(c => c.status !== 'CANCELLED')
                    .map(claim => (
                      <div key={claim.id} style={{
                        padding: '6px 8px',
                        backgroundColor: '#f1f5f9',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontWeight: 500, color: '#01B8C0' }}>{claim.id}</span>
                        <span style={{ color: '#64748b', fontSize: '12px' }}>{claim.craFinanceLender || 'No lender'}</span>
                      </div>
                    ))
                  }
                </div>

                <div style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '12px',
                  marginTop: '12px'
                }}>
                  <div style={{ marginBottom: '8px', fontSize: '14px', color: '#475569' }}>
                    <strong>ID Status:</strong>{' '}
                    {idRequestClientInfo === null ? (
                      <span style={{ color: '#9ca3af' }}>Loading...</span>
                    ) : idRequestClientInfo.verificationStatus === 'verified' ? (
                      <span style={{ color: '#059669', fontWeight: 600 }}>✓ Verified</span>
                    ) : idRequestClientInfo.verificationStatus === 'pending' ? (
                      <span style={{ color: '#d97706', fontWeight: 600 }}>⏳ Pending Review</span>
                    ) : idRequestClientInfo.verificationStatus === 'rejected' ? (
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>✗ Rejected</span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>Not Verified</span>
                    )}
                  </div>

                  {idRequestClientInfo && idRequestClientInfo.verificationStatus !== 'verified' && (
                    <div style={{ fontSize: '14px', color: '#475569' }}>
                      <IdRequestStatus
                        info={{
                          ...idRequestClientInfo,
                          clientDeceased: idRequestClientInfo.clientDeceased
                            || idRequestGroup.claims.some(claimSaysClientDeceased),
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {idRequestClientInfo?.verificationStatus === 'verified' ? (
                <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#059669', fontWeight: 500 }}>
                  This client's ID has already been verified. No request needed.
                </p>
              ) : (
                <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6b7280' }}>
                  A photo ID verification request will be sent to <strong>{idRequestGroup.email}</strong>.
                </p>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowIdRequestModal(false);
                    setIdRequestGroup(null);
                    setIdRequestClientInfo(null);
                  }}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                {idRequestClientInfo?.verificationStatus !== 'verified' && (
                  <button
                    type="button"
                    onClick={handleConfirmIdRequest}
                    disabled={idRequestLoading || idRequestClientInfo === null}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: idRequestLoading ? '#a78bfa' : '#9333ea',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: idRequestLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(147, 51, 234, 0.2)'
                    }}
                  >
                    {idRequestLoading ? 'Sending...' : 'Send ID Request'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
